// @figma file=pkCGby54zXzTsccXvHqzO6 node=15108:20335 name="Collapsible" set="mytaible-shadcn-studio" verified=2026-03-22

import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the Collapsible root.
 * Wraps Radix `CollapsiblePrimitive.Root` — all Radix props are forwarded.
 *
 * Key props:
 * - `open`         — controlled open state
 * - `defaultOpen`  — uncontrolled initial state (default: false)
 * - `onOpenChange` — called when open state changes
 * - `disabled`     — prevents the trigger from toggling the panel
 */
export interface CollapsibleProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root> {}

/**
 * Props for the CollapsibleTrigger.
 * Supports `asChild` to render as a custom element (e.g. a Button).
 */
export interface CollapsibleTriggerProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Trigger> {}

/**
 * Props for the CollapsibleContent panel.
 * Radix applies `data-state=open|closed` — use it for CSS animations.
 */
export interface CollapsibleContentProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Content> {}

// ─── Collapsible (root) ───────────────────────────────────────────────────────

/**
 * Simple show/hide container with a trigger button.
 *
 * Built on `@radix-ui/react-collapsible` with full ARIA support:
 * - Keyboard: `Space` / `Enter` on the trigger toggles the panel.
 * - ARIA: `aria-expanded` on the trigger reflects open state;
 *   `aria-controls` links trigger to content panel.
 *
 * @example
 * ```tsx
 * <Collapsible>
 *   <div className="flex items-center justify-between">
 *     <h4 className="text-sm font-medium">Repositories</h4>
 *     <CollapsibleTrigger asChild>
 *       <Button variant="ghost" size="icon">
 *         <ChevronsUpDown className="size-4" />
 *       </Button>
 *     </CollapsibleTrigger>
 *   </div>
 *   <CollapsibleContent>
 *     <p>Hidden content shown when open.</p>
 *   </CollapsibleContent>
 * </Collapsible>
 * ```
 */
const Collapsible = React.forwardRef<
  React.ComponentRef<typeof CollapsiblePrimitive.Root>,
  CollapsibleProps
>(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.Root ref={ref} className={cn('w-full', className)} {...props} />
));
Collapsible.displayName = 'Collapsible';

// ─── CollapsibleTrigger ───────────────────────────────────────────────────────

/**
 * The button (or custom element via `asChild`) that toggles the panel.
 *
 * Radix applies `aria-expanded` and `aria-controls` automatically.
 * Pair with a `ChevronsUpDown` icon (Figma: Lucide Icons / chevrons-up-down).
 */
const CollapsibleTrigger = React.forwardRef<
  React.ComponentRef<typeof CollapsiblePrimitive.Trigger>,
  CollapsibleTriggerProps
>(({ className, ...props }, ref) => (
  <CollapsiblePrimitive.Trigger
    ref={ref}
    className={cn(
      // Layout
      'flex items-center justify-between w-full',
      // Focus ring — uses design-system ring token
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-[var(--radius)]',
      // Disabled
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
CollapsibleTrigger.displayName = 'CollapsibleTrigger';

// ─── CollapsibleContent ───────────────────────────────────────────────────────

/**
 * The collapsible content panel.
 *
 * Radix sets `data-state=open|closed` — use these attributes for CSS
 * enter/exit animations. Content is clipped via `overflow-hidden`.
 */
const CollapsibleContent = React.forwardRef<
  React.ComponentRef<typeof CollapsiblePrimitive.Content>,
  CollapsibleContentProps
>(({ className, children, ...props }, ref) => (
  <CollapsiblePrimitive.Content
    ref={ref}
    className={cn(
      // Clip content during open/close
      'overflow-hidden text-sm',
      // Animate in/out via Radix data-state (Tailwind animate-in/out utilities)
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  >
    {/* Inner div provides padding so animation clips cleanly */}
    <div className="pb-4 pt-2 text-foreground">{children}</div>
  </CollapsiblePrimitive.Content>
));
CollapsibleContent.displayName = 'CollapsibleContent';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
